import {WHITELISTED_REDIRECT_URLS} from 'config/whitelistedRedirectUrls'
import {filterParamNames} from 'modules/search/constants'
import qs from 'qs'
import {mergeRight, pipe, propEq} from 'ramda'

import {parseQueryString} from '@vio/core/src/utils/url'
import {regex} from '@vio/core/src/utils/validation'

export const stringify = <T extends object>(query: T) =>
  qs.stringify(query, {
    arrayFormat: 'repeat',
    encodeValuesOnly: true
  })

const updateParameters = (
  queryString: string,
  parameters: Record<string, string>
) => {
  return stringify(mergeRight(parseQueryString(queryString), parameters))
}

/**
 * Generate a function to check whether a parameter is set to "1" in the search string
 * @example
 * ```
 * const forceLanguageChange = isParameterEnabled('forceLanguageChange')(history.location.search)
 * ```
 */
const isUrlParameterEnabled = (name: string) =>
  pipe(parseQueryString, propEq(name, '1'))

/**
 * Only allow https URLs pointing at our own domains.
 *
 * The protocol matters as much as the domain: a browser reads the hostname of
 * `javascript://vio.com/%0Aalert(1)` as `vio.com`, so checking the domain alone
 * would let ProviderRedirect run that code instead of redirecting.
 */
export const isUrlWhitelisted = (url: string): boolean => {
  try {
    const {protocol, hostname} = new URL(url)

    if (protocol !== 'https:') return false

    return WHITELISTED_REDIRECT_URLS.some(substr => regex(substr, hostname))
  } catch {
    return false
  }
}

/**
 * Check if the queryString contains the fsmr parameter (used to perform multi-room searches)
 * @param queryString: string
 * @returns boolean
 */
export const isFsmrInQueryString = (queryString: string): boolean => {
  const {fsmr} = parseQueryString(queryString)
  return Boolean(fsmr)
}

/**
 * Generates an Accommodation Page URL based on the provided hotelId, locale and searchParams.
 * @param hotelId - The ID of the hotel.
 * @param locale - The locale as string
 * @param searchParams - The URL query parameters as an object.
 * @returns The generated AP URL.
 */
export const generateAccommodationPageURL = (
  hotelId: string,
  locale: string,
  searchParams?: Record<string, unknown>,
  isAnchorHotel?: boolean,
  hasBookingHint?: boolean
): string => {
  const params = {...(searchParams || {})}

  // Get the hotelId from either the provided hotelId or the 'hotelId' parameter in searchParams
  const id: string = hotelId || (params.hotelId as string)
  const localeString: string = locale || (params.locale as string)

  // Remove 'hotelId' and 'placeId' parameters from the searchParams
  delete params.hotelId
  delete params.placeId

  // Has booking hint is used to open the search box for mobile and date confirmation tooltip on desktop
  if (hasBookingHint) params.bookingHint = '1'

  // If no valid id or locale is found, return an empty string
  if (!id || !localeString) return ''

  if (isAnchorHotel) {
    filterParamNames.forEach(name => delete params[name])
  }

  const queryString = stringify(params)

  // Construct the final URL with both the hotelId and modified query parameters
  return queryString
    ? `/Hotel/${id}/${localeString}?${queryString}`
    : `/Hotel/${id}/${localeString}`
}

const url = {
  /**
   * @deprecated Use core functions from vio/core/src/utils/url
   */
  parse: parseQueryString,
  stringify,
  updateParameters,
  isUrlParameterEnabled,
  isUrlWhitelisted
}

export const removeUrlParams = (value: string, names: string[]) => {
  const [base, hash] = value.split('#')
  const [path, query = ''] = base.split('?')
  const searchParams = new URLSearchParams(query)
  names.forEach(name => searchParams.delete(name))
  const cleaned = searchParams.toString()
  return `${path}${cleaned ? `?${cleaned}` : ''}${hash ? `#${hash}` : ''}`
}

const removeUrlParamsFrom = (value: unknown, names: string[]) =>
  typeof value === 'string' ? removeUrlParams(value, names) : value

/**
 * Drops one-shot params (deep links, SRP hand-offs) from the current history
 * entry without a router round-trip, so a synchronous follow-up read of the
 * URL already sees them gone.
 */
export const stripUrlParamsFromHistory = (names: string[]) => {
  const cleanUrl = removeUrlParams(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
    names
  )
  // Next stores its own url/as in history.state; strip the params there
  // too or back/forward restores them from the stale entry
  const currentState = window.history.state
  const nextState = currentState?.__N
    ? {
        ...currentState,
        url: removeUrlParamsFrom(currentState.url, names),
        as: removeUrlParamsFrom(currentState.as, names)
      }
    : currentState
  window.history.replaceState(nextState, '', cleanUrl)
}

export default url
